import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneRight, Paperclip, DownloadSimple, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

function isImage(t) { return (t || "").startsWith("image/"); }

function Attachment({ msg, mine }) {
  const [imgUrl, setImgUrl] = useState(null);
  const image = isImage(msg.attachment_type);

  useEffect(() => {
    let revoke;
    if (image) {
      api.get(`/chat/attachment/${msg.id}`, { responseType: "blob" })
        .then((res) => { const u = URL.createObjectURL(res.data); revoke = u; setImgUrl(u); })
        .catch(() => {});
    }
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [msg.id]);

  const download = async () => {
    try {
      const res = await api.get(`/chat/attachment/${msg.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = msg.attachment_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  if (image) {
    return (
      <button data-testid="chat-attachment-image" onClick={download} className="block">
        {imgUrl ? <img src={imgUrl} alt={msg.attachment_name} className="max-w-[220px] max-h-[220px] rounded-sm object-cover" />
          : <div className="w-40 h-24 bg-black/5 rounded-sm animate-pulse" />}
      </button>
    );
  }
  return (
    <button data-testid="chat-attachment-file" onClick={download}
      className={`flex items-center gap-2 text-sm ${mine ? "text-white" : "text-[#002FA7]"}`}>
      <FileText size={20} weight="fill" />
      <span className="truncate max-w-[160px]">{msg.attachment_name}</span>
      <DownloadSimple size={16} />
    </button>
  );
}

export function ChatThread({ clientEmail, height = "h-[420px]" }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const endRef = useRef();
  const fileRef = useRef();
  const url = clientEmail ? `/chat?client_email=${encodeURIComponent(clientEmail)}` : "/chat";

  const load = () => api.get(url).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [clientEmail]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post("/chat", clientEmail ? { text, client_email: clientEmail } : { text });
    setText(""); load();
  };

  const sendFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File exceeds 15MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    const path = clientEmail ? `/chat/attachment?client_email=${encodeURIComponent(clientEmail)}` : "/chat/attachment";
    try {
      await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="card flex flex-col">
      <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${height}`}>
        {msgs.length === 0 && <p className="text-sm text-[#888]">No messages yet. Say hello 👋</p>}
        {msgs.map((m, i) => {
          const mine = m.sender_email === user?.email;
          return (
            <div key={m.id || i} data-testid={`chat-msg-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3 py-2 text-sm rounded-sm ${mine ? "bg-[#002FA7] text-white" : "bg-black/[0.05] border border-black/10"}`}>
                {!mine && <p className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{m.sender_name} · {m.sender_role}</p>}
                {m.attachment_path ? <Attachment msg={m} mine={mine} /> : m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-black/10 p-3 flex gap-2">
        <input ref={fileRef} type="file" hidden data-testid="chat-file-input"
          onChange={(e) => sendFile(e.target.files[0])} />
        <button type="button" data-testid="chat-attach-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="border border-black/20 hover:bg-black/5 px-3 rounded-sm transition-colors disabled:opacity-50" title="Attach file">
          <Paperclip size={18} weight={uploading ? "fill" : "regular"} />
        </button>
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={uploading ? "Uploading…" : "Type a message…"}
          className="flex-1 border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <button data-testid="chat-send" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 rounded-sm transition-colors"><PaperPlaneRight size={18} weight="fill" /></button>
      </form>
    </div>
  );
}
